import React, { useCallback, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { useParams } from 'react-router-dom';

import { usePromiseState } from '@futrli/shared-services';
import { BasicAssumption, PreviewAssumptionImpactsResponse } from '@futrli-sdk/predict';

import { AccountsImpactedContainer } from '../../../components/accounts-impacted/accounts-impacted-container';
import { FormulaReferenceOption } from '../../../../../utils/formulas/types';
import { getPredictionTypeArg } from '../../../context/utils';
import { previewImpacts } from '../../../../../core/api/assumption';
import { PredictionType, PreviewImpactsRequestBody, paymentTreatmentTypes } from '../../../types';
import { useStandardOrganisationPredictionBuilderContext } from '../../../context/standard-organisation-prediction-builder-context';
import { usePredictionsInfoContext } from '../../../../detail/context/predictions-info-context';
import { usePredictManagementContext } from '../../../../../predict-management/context/predict-management-context';

const DEBOUNCE_WAIT = 500;

interface Props {
  formulaEditorOptions?: FormulaReferenceOption[];
}

export const ImpactPreview = ({ formulaEditorOptions }: Props) => {
  const { addErrorMessage } = usePredictManagementContext();
  const { forecast } = usePredictionsInfoContext();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisatioId } = useParams();

  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisatioId || '';

  const {
    impactPreviewParams,
    isCashFlowDisabled,
    isQuickCopy,
    hideAccountsImpacted,
    paymentDataParams,
    paymentTreatment,
    predictionType,
    urlPredictionId,
    handlePreviewImpactResponseChange,
    isEditing,
    predictionData,
    setIsSaveDisabled
  } = useStandardOrganisationPredictionBuilderContext();

  const advisorFormula =
    isEditing && predictionType === PredictionType.FREESTYLE
      ? (predictionData as BasicAssumption).advisor_formula
      : undefined;

  const [impactPreviewReq, setImpactPreviewReq, impactPreview] = usePromiseState<PreviewAssumptionImpactsResponse>();

  const forecastId = forecast?.id;

  const fetchImpactPreview = useMemo(() => {
    return debounce(async (requestData: PreviewImpactsRequestBody) => {
      if (forecastId) {
        let request;

        try {
          request = previewImpacts<PreviewAssumptionImpactsResponse>(
            workspaceId,
            organisationId,
            forecastId,
            requestData
          );

          const newPreviewImpacts = await setImpactPreviewReq(request);
          setIsSaveDisabled(!newPreviewImpacts.is_valid);
          handlePreviewImpactResponseChange(newPreviewImpacts);
        } catch (e) {
          if (impactPreviewReq.promise.current === request) {
            addErrorMessage?.(e as Error);
          }
        }
      }
    }, DEBOUNCE_WAIT);
  }, [
    addErrorMessage,
    forecastId,
    impactPreviewReq.promise,
    organisationId,
    setImpactPreviewReq,
    workspaceId,
    handlePreviewImpactResponseChange,
    setIsSaveDisabled
  ]);

  const loadImpactPreview = useCallback(() => {
    if (impactPreviewParams && !isCashFlowDisabled) {
      const requestData: PreviewImpactsRequestBody = {
        assumption_id: urlPredictionId && !isQuickCopy ? urlPredictionId : undefined,
        assumption_type: getPredictionTypeArg(predictionType),
        assumption_data: impactPreviewParams,
        payment_type: paymentTreatmentTypes[paymentTreatment],
        payment_data: paymentDataParams
      };

      fetchImpactPreview(requestData);
    }
  }, [
    fetchImpactPreview,
    impactPreviewParams,
    isCashFlowDisabled,
    isQuickCopy,
    paymentDataParams,
    paymentTreatment,
    predictionType,
    urlPredictionId
  ]);

  // Load impact data (if params change)
  useEffect(() => {
    loadImpactPreview();
  }, [loadImpactPreview]);

  if (hideAccountsImpacted) {
    return null;
  }

  return (
    <AccountsImpactedContainer
      predictionType={predictionType}
      previewData={impactPreview}
      advisorFormula={advisorFormula}
      formulaEditorOptions={formulaEditorOptions}
    />
  );
};
